import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import type { Product } from '@/types/product'
import { logger } from '@/utils/logger'
import { cartService, type CartResponse, type CartItemResponse } from '@/services/business/cartService'

// 購物車商品項目介面
export interface CartItem {
  /** 伺服器端購物車項目 ID (可能為空，表示僅在本地) */
  cartItemId?: string
  /** 商品 UID */
  productId: string
  /** 商品資訊 */
  product: Product
  /** 數量 */
  quantity: number
  /** 加入時間 */
  addedAt: Date
  /** 是否僅在本地 (未同步到伺服器) */
  localOnly?: boolean
}

// 按店家分組的購物車項目
export interface VendorCartGroup {
  /** 店家 ID */
  vendorId: string
  /** 店家名稱 */
  vendorName: string
  /** 店家商品列表 */
  items: CartItem[]
  /** 店家商品總點數 */
  totalPoints: number
  /** 店家商品總數量 */
  totalQuantity: number
}

export const useCartStore = defineStore('cart', () => {
  // 購物車狀態
  const items = ref<CartItem[]>([])
  const isLoading = ref(false)
  const isSyncing = ref(false)
  const lastSyncTime = ref<Date | null>(null)

  // 計算屬性
  const totalItems = computed(() => {
    return items.value.reduce((total, item) => total + item.quantity, 0)
  })

  const totalPoints = computed(() => {
    return items.value.reduce((total, item) => {
      if (item.product.unlimitedPoints) {
        return total // 無限點數商品不計算點數
      }
      return total + (item.product.points * item.quantity)
    }, 0)
  })

  const hasItems = computed(() => items.value.length > 0)

  const hasUnlimitedPointsItems = computed(() => {
    return items.value.some(item => item.product.unlimitedPoints)
  })

  const hasLocalOnlyItems = computed(() => {
    return items.value.some(item => item.localOnly === true)
  })

  const needsSync = computed(() => {
    return hasLocalOnlyItems.value || !lastSyncTime.value
  })

  // 按店家分組
  const vendorGroups = computed((): VendorCartGroup[] => {
    const groups = new Map<string, VendorCartGroup>()
    
    items.value.forEach(item => {
      const vendorId = item.product.vendorId
      const vendorName = item.product.vendorName || '未知店家'
      
      if (!groups.has(vendorId)) {
        groups.set(vendorId, {
          vendorId,
          vendorName,
          items: [],
          totalPoints: 0,
          totalQuantity: 0
        })
      }
      
      const group = groups.get(vendorId)!
      group.items.push(item)
      group.totalQuantity += item.quantity
      
      if (!item.product.unlimitedPoints) {
        group.totalPoints += item.product.points * item.quantity
      }
    })
    
    return Array.from(groups.values()).sort((a, b) => a.vendorName.localeCompare(b.vendorName))
  })

  // 檢查商品是否已在購物車中
  const isInCart = (productId: string): boolean => {
    return items.value.some(item => item.productId === productId)
  }

  // 獲取商品在購物車中的數量
  const getQuantity = (productId: string): number => {
    const item = items.value.find(item => item.productId === productId)
    return item?.quantity || 0
  }

  // 獲取購物車項目
  const getItem = (productId: string): CartItem | undefined => {
    return items.value.find(item => item.productId === productId)
  }

  // 將伺服器回應轉換為本地購物車項目
  const convertServerResponseToCartItem = (serverItem: CartItemResponse): CartItem => {
    return {
      cartItemId: serverItem.itemId.toString(),
      productId: serverItem.product?.uid || serverItem.productId.toString(),
      product: {
        uid: serverItem.product?.uid || serverItem.productId.toString(),
        name: serverItem.product?.name || '未知商品',
        description: serverItem.product?.description || '',
        points: serverItem.product?.points || 0,
        unlimitedPoints: serverItem.product?.unlimitedPoints || false,
        mainImageUrl: serverItem.product?.mainImageUrl,
        vendorId: serverItem.product?.vendorId || '',
        vendorName: serverItem.product?.vendorName || '未知店家',
        status: serverItem.product?.status || 'UNKNOWN',
        createdAt: serverItem.createdAt,
        updatedAt: serverItem.updatedAt
      } as Product,
      quantity: serverItem.quantity,
      addedAt: new Date(serverItem.createdAt),
      localOnly: false
    }
  }

  // 加入商品到購物車
  const addToCart = async (product: Product, quantity: number = 1): Promise<boolean> => {
    try {
      logger.info('加入商品到購物車', { productId: product.uid, quantity })
      
      const existingItemIndex = items.value.findIndex(item => item.productId === product.uid)
      
      if (existingItemIndex !== -1) {
        // 商品已存在，更新數量
        const existingItem = items.value[existingItemIndex]
        const newQuantity = existingItem.quantity + quantity
        
        // 檢查數量限制 (最大10個)
        if (newQuantity > 10) {
          logger.warn('商品數量超過限制', { productId: product.uid, currentQuantity: existingItem.quantity, addQuantity: quantity })
          return false
        }
        
        return await updateQuantity(product.uid, newQuantity)
      } else {
        // 新商品，添加到購物車
        const cartItem: CartItem = {
          productId: product.uid,
          product: { ...product }, // 深拷貝避免引用問題
          quantity,
          addedAt: new Date(),
          localOnly: true // 標記為僅本地，待同步
        }
        
        items.value.push(cartItem)
        
        // 保存到 localStorage
        saveToStorage()
        
        // 嘗試同步到伺服器
        syncToServer(cartItem)
        
        logger.info('商品成功加入購物車', { 
          productId: product.uid, 
          totalItems: totalItems.value,
          totalPoints: totalPoints.value 
        })
        
        return true
      }
    } catch (error) {
      logger.error('加入購物車失敗', { productId: product.uid, error })
      return false
    }
  }

  // 同步單個項目到伺服器
  const syncToServer = async (cartItem: CartItem): Promise<void> => {
    try {
      if (!cartItem.localOnly) return
      
      const response = await cartService.addToCart(cartItem.productId, cartItem.quantity)
      
      if (response.success && response.data) {
        // 更新本地項目，移除 localOnly 標記
        const index = items.value.findIndex(item => item.productId === cartItem.productId)
        if (index !== -1) {
          items.value[index].cartItemId = response.data.id
          items.value[index].localOnly = false
          saveToStorage()
        }
        
        logger.info('購物車項目已同步到伺服器', { 
          productId: cartItem.productId,
          cartItemId: response.data.id 
        })
      }
    } catch (error) {
      logger.warn('同步到伺服器失敗，保持本地狀態', { 
        productId: cartItem.productId, 
        error 
      })
    }
  }

  // 更新商品數量
  const updateQuantity = async (productId: string, quantity: number): Promise<boolean> => {
    try {
      if (quantity < 0) {
        logger.warn('商品數量不能為負數', { productId, quantity })
        return false
      }
      
      if (quantity === 0) {
        return await removeFromCart(productId)
      }
      
      if (quantity > 10) {
        logger.warn('商品數量超過限制', { productId, quantity })
        return false
      }
      
      const itemIndex = items.value.findIndex(item => item.productId === productId)
      
      if (itemIndex === -1) {
        logger.warn('商品不在購物車中', { productId })
        return false
      }
      
      const item = items.value[itemIndex]
      const oldQuantity = item.quantity
      
      // 更新本地數量
      items.value[itemIndex].quantity = quantity
      saveToStorage()
      
      // 同步到伺服器
      if (item.cartItemId && !item.localOnly) {
        try {
          await cartService.updateCartItem(item.cartItemId, quantity)
        } catch (error) {
          // 如果伺服器更新失敗，標記為僅本地
          items.value[itemIndex].localOnly = true
          logger.warn('伺服器更新失敗，標記為本地狀態', { productId, error })
        }
      }
      
      logger.info('商品數量已更新', { productId, oldQuantity, newQuantity: quantity })
      return true
    } catch (error) {
      logger.error('更新商品數量失敗', { productId, quantity, error })
      return false
    }
  }

  // 從購物車移除商品
  const removeFromCart = async (productId: string): Promise<boolean> => {
    try {
      const itemIndex = items.value.findIndex(item => item.productId === productId)
      
      if (itemIndex === -1) {
        logger.warn('商品不在購物車中', { productId })
        return false
      }
      
      const item = items.value[itemIndex]
      
      // 從本地移除
      items.value.splice(itemIndex, 1)
      saveToStorage()
      
      // 從伺服器移除
      if (item.cartItemId && !item.localOnly) {
        try {
          await cartService.removeFromCart(item.cartItemId)
        } catch (error) {
          logger.warn('伺服器移除失敗，但本地已移除', { productId, error })
        }
      }
      
      logger.info('商品已從購物車移除', { productId })
      return true
    } catch (error) {
      logger.error('移除商品失敗', { productId, error })
      return false
    }
  }

  // 清空購物車
  const clearCart = async (): Promise<void> => {
    try {
      const itemCount = items.value.length
      
      // 清空本地
      items.value = []
      saveToStorage()
      
      // 清空伺服器
      try {
        await cartService.clearCart()
      } catch (error) {
        logger.warn('伺服器清空失敗，但本地已清空', { error })
      }
      
      logger.info('購物車已清空', { removedItems: itemCount })
    } catch (error) {
      logger.error('清空購物車失敗', { error })
    }
  }

  // 移除特定店家的所有商品
  const removeVendorItems = async (vendorId: string): Promise<boolean> => {
    try {
      const vendorItems = items.value.filter(item => item.product.vendorId === vendorId)
      const originalLength = items.value.length
      
      // 從本地移除
      items.value = items.value.filter(item => item.product.vendorId !== vendorId)
      const removedCount = originalLength - items.value.length
      
      if (removedCount > 0) {
        saveToStorage()
        
        // 從伺服器移除
        try {
          await cartService.removeVendorItems(vendorId)
        } catch (error) {
          logger.warn('伺服器移除店家商品失敗，但本地已移除', { vendorId, error })
        }
        
        logger.info('店家商品已移除', { vendorId, removedCount })
        return true
      }
      
      return false
    } catch (error) {
      logger.error('移除店家商品失敗', { vendorId, error })
      return false
    }
  }

  // 從伺服器載入購物車
  const loadFromServer = async (): Promise<void> => {
    try {
      isLoading.value = true
      logger.debug('開始從伺服器載入購物車')
      
      const response = await cartService.getCart()
      logger.debug('收到購物車API回應', { response })
      
      if (response.success && response.data) {
        // 確保 items 存在，如果不存在則使用空陣列
        const serverItemsData = response.data.items || []
        logger.debug('伺服器購物車項目', { itemsCount: serverItemsData.length })
        
        // 轉換伺服器資料為本地格式
        const serverItems = serverItemsData.map(convertServerResponseToCartItem)
        logger.debug('轉換後的伺服器項目', { serverItems })
        
        // 合併本地僅有的項目
        const localOnlyItems = items.value.filter(item => item.localOnly === true)
        logger.debug('本地項目', { localOnlyItems })
        
        items.value = [...serverItems, ...localOnlyItems]
        lastSyncTime.value = new Date()
        saveToStorage()
        
        logger.info('購物車已從伺服器載入', { 
          serverItems: serverItems.length,
          localOnlyItems: localOnlyItems.length,
          totalItems: totalItems.value 
        })
      } else {
        logger.warn('購物車API回應失敗', { response })
      }
    } catch (error) {
      logger.error('從伺服器載入購物車失敗', { error, stack: error instanceof Error ? error.stack : 'No stack' })
    } finally {
      isLoading.value = false
    }
  }

  // 同步本地資料到伺服器
  const syncToServerAll = async (): Promise<void> => {
    try {
      const localOnlyItems = items.value.filter(item => item.localOnly === true)
      
      if (localOnlyItems.length === 0) {
        logger.info('沒有需要同步的本地項目')
        return
      }
      
      isSyncing.value = true
      
      const response = await cartService.syncLocalCart(localOnlyItems)
      
      if (response.success && response.data) {
        // 更新本地項目，移除 localOnly 標記
        const syncedItems = response.data.items.map(convertServerResponseToCartItem)
        
        // 替換本地項目
        const nonLocalItems = items.value.filter(item => !item.localOnly)
        items.value = [...nonLocalItems, ...syncedItems]
        
        lastSyncTime.value = new Date()
        saveToStorage()
        
        logger.info('本地購物車已同步到伺服器', { 
          syncedItems: syncedItems.length 
        })
      }
    } catch (error) {
      logger.error('同步到伺服器失敗', { error })
    } finally {
      isSyncing.value = false
    }
  }

  // 保存到 localStorage
  const saveToStorage = (): void => {
    try {
      const cartData = {
        items: items.value,
        timestamp: Date.now()
      }
      localStorage.setItem('chentrade_cart', JSON.stringify(cartData))
    } catch (error) {
      logger.error('保存購物車到本地存儲失敗', { error })
    }
  }

  // 從 localStorage 載入
  const loadFromStorage = (): void => {
    try {
      const cartData = localStorage.getItem('chentrade_cart')
      
      if (!cartData) {
        logger.debug('本地存儲中沒有購物車資料')
        return
      }
      
      const parsed = JSON.parse(cartData)
      
      // 檢查資料是否過期 (7天)
      const sevenDaysInMs = 7 * 24 * 60 * 60 * 1000
      if (Date.now() - parsed.timestamp > sevenDaysInMs) {
        logger.info('購物車資料已過期，清除本地存儲')
        localStorage.removeItem('chentrade_cart')
        return
      }
      
      // 恢復購物車資料
      if (Array.isArray(parsed.items)) {
        items.value = parsed.items.map((item: any) => ({
          ...item,
          addedAt: new Date(item.addedAt)
        }))
        
        logger.info('購物車資料已從本地存儲載入', { 
          itemCount: items.value.length,
          totalItems: totalItems.value 
        })
      }
    } catch (error) {
      logger.error('載入購物車資料失敗', { error })
      // 清除損壞的資料
      localStorage.removeItem('chentrade_cart')
    }
  }

  // 初始化 (載入本地存儲的資料，然後嘗試從伺服器同步)
  const initialize = async (): Promise<void> => {
    // 先載入本地資料
    loadFromStorage()
    
    // 嘗試從伺服器載入和同步
    try {
      await loadFromServer()
      await syncToServerAll()
    } catch (error) {
      logger.warn('初始化時伺服器同步失敗，使用本地資料', { error })
    }
  }


  return {
    // 狀態
    items,
    isLoading,
    isSyncing,
    lastSyncTime,
    
    // 計算屬性
    totalItems,
    totalPoints,
    hasItems,
    hasUnlimitedPointsItems,
    hasLocalOnlyItems,
    needsSync,
    vendorGroups,
    
    // 查詢方法
    isInCart,
    getQuantity,
    getItem,
    
    // 操作方法 (現在都是 async)
    addToCart,
    updateQuantity,
    removeFromCart,
    clearCart,
    removeVendorItems,
    
    // 同步方法
    loadFromServer,
    syncToServerAll,
    
    // 工具方法
    initialize,
    saveToStorage,
    loadFromStorage
  }
})